import is from '@sindresorhus/is';
import partition from 'lodash/partition';
import type { FunctionComponent } from 'react';
import { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router';

import { useApplicationContext } from '~/shared/context/use-application-context';

import { parseUrlTileValues } from '../parse-url-tiles';
import type { UrlTileDefinition } from '../types';
import { URL_TILE_SEARCH_PARAM } from '../types';

const isParsableTileValue = (value: string, definitions: readonly UrlTileDefinition[]): boolean =>
    is.nonEmptyArray(parseUrlTileValues([value], definitions));

type UrlTileAvailabilityCleanupProps = {
    readonly allDefinitions: readonly UrlTileDefinition[];
    readonly availableDefinitions: readonly UrlTileDefinition[];
    readonly declaredTypes: ReadonlySet<string>;
};

export const UrlTileAvailabilityCleanup: FunctionComponent<UrlTileAvailabilityCleanupProps> = ({
    allDefinitions,
    availableDefinitions,
    declaredTypes,
}) => {
    const location = useLocation();
    const [, setSearchParams] = useSearchParams();
    const { logger } = useApplicationContext();

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const values = searchParams.getAll(URL_TILE_SEARCH_PARAM);
        const [keptValues, droppedValues] = partition(values, (value) =>
            isParsableTileValue(value, availableDefinitions),
        );

        if (is.emptyArray(droppedValues)) {
            return;
        }

        for (const value of droppedValues) {
            const undeclaredType = parseUrlTileValues([value], allDefinitions)
                .map((tile) => tile.definition.type)
                .find((type) => !declaredTypes.has(type));

            if (!is.undefined(undeclaredType)) {
                logger?.warn(
                    `[url-tiles] stripping tile "${value}": type "${undeclaredType}" is not declared for route "${location.pathname}"`,
                );
            }
        }

        searchParams.delete(URL_TILE_SEARCH_PARAM);

        for (const value of keptValues) {
            searchParams.append(URL_TILE_SEARCH_PARAM, value);
        }

        const timeoutId = globalThis.setTimeout(() => {
            setSearchParams(searchParams, { replace: true });
        }, 0);

        return () => globalThis.clearTimeout(timeoutId);
    }, [allDefinitions, availableDefinitions, declaredTypes, location, logger, setSearchParams]);

    return null;
};
